import { DIRECTIONS, KEYS, TOUCH_CONFIG } from './constants.js';

export class InputManager {
    constructor(game) {
        this.game = game;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchStartTime = 0;
        this.lastTouchTime = 0;
        this.isTouchDevice = 'ontouchstart' in window;
        
        this.setupKeyboardControls();
        this.setupTouchControls();
        this.setupGamepadControls();
    }
    
    setupKeyboardControls() {
        document.addEventListener('keydown', (e) => {
            if (this.game.getGameState() !== 'playing') return;
            
            e.preventDefault();
            this.handleKeyboardInput(e);
        });
        
        // Prevent default behavior for game keys
        document.addEventListener('keydown', (e) => {
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space'].includes(e.code)) {
                e.preventDefault();
            }
        });
    }
    
    handleKeyboardInput(e) {
        const directionMap = {
            [KEYS.ARROW_UP]: DIRECTIONS.UP,
            [KEYS.W]: DIRECTIONS.UP,
            [KEYS.ARROW_DOWN]: DIRECTIONS.DOWN,
            [KEYS.S]: DIRECTIONS.DOWN,
            [KEYS.ARROW_LEFT]: DIRECTIONS.LEFT,
            [KEYS.A]: DIRECTIONS.LEFT,
            [KEYS.ARROW_RIGHT]: DIRECTIONS.RIGHT,
            [KEYS.D]: DIRECTIONS.RIGHT
        };
        
        const direction = directionMap[e.code];
        if (direction) {
            this.game.handleInput(this.getDirectionName(direction));
        }
        
        // Handle special keys
        switch (e.code) {
            case KEYS.SPACE:
                this.game.togglePause();
                break;
            case KEYS.ESCAPE:
                this.game.pauseGame();
                break;
        }
    }
    
    setupTouchControls() {
        if (!this.isTouchDevice) return;
        
        const canvas = document.getElementById('game-canvas');
        
        // Touch events
        canvas.addEventListener('touchstart', (e) => {
            e.preventDefault();
            const touch = e.touches[0];
            this.touchStartX = touch.clientX;
            this.touchStartY = touch.clientY;
            this.touchStartTime = Date.now();
        }, { passive: false });
        
        canvas.addEventListener('touchend', (e) => {
            e.preventDefault();
            const touch = e.changedTouches[0];
            this.handleSwipe(touch.clientX, touch.clientY);
        }, { passive: false });
        
        // Mouse events for desktop testing
        canvas.addEventListener('mousedown', (e) => {
            this.touchStartX = e.clientX;
            this.touchStartY = e.clientY;
            this.touchStartTime = Date.now();
        });
        
        canvas.addEventListener('mouseup', (e) => {
            this.handleSwipe(e.clientX, e.clientY);
        });
        
        // Prevent context menu on long press
        canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    }
    
    handleSwipe(endX, endY) {
        const deltaX = endX - this.touchStartX;
        const deltaY = endY - this.touchStartY;
        const deltaTime = Date.now() - this.touchStartTime;
        
        // Check if it's a valid swipe
        if (deltaTime > TOUCH_CONFIG.MAX_TAP_TIME) return;
        
        const absDeltaX = Math.abs(deltaX);
        const absDeltaY = Math.abs(deltaY);
        
        // Determine if it's a swipe or tap
        if (absDeltaX < TOUCH_CONFIG.MIN_SWIPE_DISTANCE && absDeltaY < TOUCH_CONFIG.MIN_SWIPE_DISTANCE) {
            // It's a tap - could be used for pause or other actions
            this.handleTap();
            return;
        }
        
        // Determine swipe direction
        if (absDeltaX > absDeltaY) {
            // Horizontal swipe
            if (deltaX > 0) {
                this.game.handleInput('right');
            } else {
                this.game.handleInput('left');
            }
        } else {
            // Vertical swipe
            if (deltaY > 0) {
                this.game.handleInput('down');
            } else {
                this.game.handleInput('up');
            }
        }
    }
    
    handleTap() {
        // Double tap to pause
        const currentTime = Date.now();
        if (currentTime - this.lastTouchTime < TOUCH_CONFIG.DOUBLE_TAP_DELAY) {
            this.game.togglePause();
        }
        this.lastTouchTime = currentTime;
    }
    
    setupGamepadControls() {
        // Gamepad support for future enhancement
        window.addEventListener('gamepadconnected', (e) => {
            console.log('Gamepad connected:', e.gamepad.id);
        });
        
        window.addEventListener('gamepaddisconnected', (e) => {
            console.log('Gamepad disconnected:', e.gamepad.id);
        });
    }
    
    updateGamepadInput() {
        // Poll gamepad state
        const gamepads = navigator.getGamepads();
        if (!gamepads[0]) return;
        
        const gamepad = gamepads[0];
        
        // D-pad or left stick
        const leftStickX = gamepad.axes[0];
        const leftStickY = gamepad.axes[1];
        
        // Check for significant input
        if (Math.abs(leftStickX) > 0.5 || Math.abs(leftStickY) > 0.5) {
            if (Math.abs(leftStickX) > Math.abs(leftStickY)) {
                if (leftStickX > 0.5) {
                    this.game.handleInput('right');
                } else if (leftStickX < -0.5) {
                    this.game.handleInput('left');
                }
            } else {
                if (leftStickY > 0.5) {
                    this.game.handleInput('down');
                } else if (leftStickY < -0.5) {
                    this.game.handleInput('up');
                }
            }
        }
        
        // Button presses
        if (gamepad.buttons[0] && gamepad.buttons[0].pressed) {
            this.game.togglePause();
        }
    }
    
    getDirectionName(direction) {
        if (direction.x === 0 && direction.y === -1) return 'up';
        if (direction.x === 0 && direction.y === 1) return 'down';
        if (direction.x === -1 && direction.y === 0) return 'left';
        if (direction.x === 1 && direction.y === 0) return 'right';
        return 'up'; // default
    }
    
    // Method to be called in game loop for gamepad input
    update() {
        this.updateGamepadInput();
    }
    
    // Prevent scrolling on mobile
    preventScroll() {
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';
    }
    
    allowScroll() {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
    }
}
